import React, { useRef, useState, PropsWithChildren, useEffect, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react-lite';

import { CommentContext, CommentContextProps, CommentSortOrder } from './comment.context';
import { getCurrentMember } from '../../../lib/utils/get-current-member';
import { catchError } from '../../catch-error';

import type { Comment } from '../../../app/entities/comment';
import type { Member } from '../../../app/entities';

type CommentProviderType = {
  comments: CommentContextProps['comments'];
};

export const sortComments = (comments: Comment[], sortOrder: CommentSortOrder): Comment[] => {
  return [...comments].sort((a, b) => {
    if (sortOrder === 'timestamp') {
      // Comments without timestamps go to the end
      if (a.timestamp == null && b.timestamp == null) return 0;
      if (a.timestamp == null) return 1;
      if (b.timestamp == null) return -1;
      return a.timestamp - b.timestamp;
    }
    // Sort by createdAt: ascending (oldest first) or descending (newest first)
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return sortOrder === '-createdAt' ? dateB - dateA : dateA - dateB;
  });
};

export const CommentProvider: React.FC<PropsWithChildren<CommentProviderType>> = observer(
  ({ children, comments: baseComments }) => {
    const commentsListRef = useRef<HTMLDivElement>(null);
    const playerRef = useRef<HTMLVideoElement | null>(null);
    const member = getCurrentMember();

    const [selectedComment, setSelectedComment] = useState<Comment>();
    const [comments, setComments] = useState<Comment[]>(baseComments);
    const [pdfCurrentPage, setPdfCurrentPage] = useState<number>(0);
    // A choice made in this session always wins over the stored preference, which
    // may only resolve after the first click — deriving state from it instead of
    // duplicating it means a member load can no longer undo that click (PRO-1029)
    const [chosenSortOrder, setChosenSortOrder] = useState<CommentSortOrder>();

    const sortOrder = chosenSortOrder ?? member?.preferences?.commentSortOrder ?? 'timestamp';

    useEffect(() => {
      setComments(baseComments);
    }, [baseComments]);

    const setSortOrder = useCallback(
      async (order: CommentSortOrder) => {
        setChosenSortOrder(order);

        if (!member) {
          return;
        }

        try {
          await member.update({
            preferences: {
              ...member.preferences,
              commentSortOrder: order,
            },
          } as Partial<Omit<Member, '_id'>>);
        } catch (e) {
          catchError(e);
        }
      },
      [member],
    );

    const sortedComments = useMemo(() => sortComments(comments, sortOrder), [comments, sortOrder]);

    const selectComment = (comment?: Comment) => {
      if (!comment) {
        setSelectedComment(undefined);
        return;
      }

      setSelectedComment(comment);

      if (!commentsListRef?.current) {
        return;
      }

      const element = (Array.from(commentsListRef.current.children) as HTMLDivElement[]).find(
        (el) => el.id === comment._id,
      );

      if (!element) {
        return false;
      }

      commentsListRef.current.scrollTop = element.offsetTop - commentsListRef.current.offsetTop;
    };

    const addComment = (comment: Comment) => {
      const finalComments = comments.concat(comment);

      setComments(finalComments);
    };

    const deleteComment = async (id: string) => {
      const finalComments = comments.filter((comment) => comment._id !== id);

      setComments(finalComments);
    };

    const updateComment = async (comment: Comment) => {
      const finalComments = [...comments];
      const targetCommentIndex = finalComments.findIndex((el) => el._id === comment._id);
      finalComments[targetCommentIndex] = comment;

      setComments(finalComments);
    };

    return (
      <CommentContext.Provider
        value={{
          comments: sortedComments,
          selectComment,
          selectedComment: selectedComment,
          addComment,
          deleteComment,
          updateComment,
          commentsListRef,
          playerRef,
          pdfCurrentPage,
          setPdfCurrentPage,
          sortOrder,
          setSortOrder,
        }}
      >
        {children}
      </CommentContext.Provider>
    );
  },
);
